import { Notification } from '@api/READ_ONLY/notifications_api/Api';
import QUERY_KEYS from '@app/common/queryKeys';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

interface UseInfiniteNotificationsProps {
  userId?: number;
  isRead?: boolean | null;
  searchTerm?: string;
  // actionValue?: 'accept' | 'reject' | null; // dropdown filter
}

type NotificationsPagingResponse = {
  data: Notification[];
  total: number;
  offset: number;
  limit: number;
};

const useOptimisticUpdates = (props: UseInfiniteNotificationsProps) => {
  const queryClient = useQueryClient();

  const userId = props?.userId;

  // Full notification pages - Optimistic updates

  const optimisticMarkAllAsReadForFullNotificationPage = () => {
    queryClient.setQueriesData<InfiniteData<NotificationsPagingResponse>>(
      {
        predicate: (q) => {
          const k = q.queryKey as unknown as (string | number)[];
          return k[0] === QUERY_KEYS.NOTIFICATIONS_LIST_FULL && k[1] === 'infinite' && k[2] === userId;
        },
      },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            data: p.data.map((n) => (n.isRead ? n : { ...n, isRead: true })),
          })),
        };
      }
    );
  };

  const optimisticMarkOneAsReadForFullNotificationPage = (notificationId: number) => {
    queryClient.setQueriesData<InfiniteData<NotificationsPagingResponse>>(
      { queryKey: [QUERY_KEYS.NOTIFICATIONS_LIST_FULL, 'infinite', userId] },
      (old) => {
        if (!old) return old;

        let changed = false;

        const pages = old.pages.map((p) => ({
          ...p,
          data: p.data.map((item) => {
            if (item.notificationId === notificationId && !item.isRead) {
              changed = true;
              return {
                ...item,
                isRead: true,
              };
            }
            return item;
          }),
        }));

        if (!changed) return old; // keep referential equality if nothing changed
        return { ...old, pages };
      }
    );
  };

  // Widget notifications  - Optimistic updates

  const optimisticMarkOneAsReadForWidgetNotifications = (notificationId: number) => {
    queryClient.setQueryData<Notification[]>([QUERY_KEYS.NOTIFICATIONS_WIDGET, userId], (old) => {
      if (!old) return old;

      let changed = false;

      const next = old.map((item) => {
        if (item.notificationId === notificationId && !item.isRead) {
          changed = true;
          return { ...item, isRead: true };
        }
        return item;
      });
      // keep referential equality if nothing changed
      return changed ? next : old;
    });
  };

  const optimisticMarkAllAsReadForWidgetNotifications = () => {
    queryClient.setQueryData<Notification[]>([QUERY_KEYS.NOTIFICATIONS_WIDGET, userId], (old) => {
      if (!old) return old;

      return old.map((item) => {
        if (!item.isRead) {
          return { ...item, isRead: true };
        }
        return item;
      });
    });
  };

  return {
    optimisticMarkAllAsReadForFullNotificationPage,
    optimisticMarkAllAsReadForWidgetNotifications,
    optimisticMarkOneAsReadForFullNotificationPage,
    optimisticMarkOneAsReadForWidgetNotifications,
  };
};

export default useOptimisticUpdates;
